import React from 'react';
import { useTranslation } from 'react-i18next';
import { ModalWrapper, ModalCloseButton, Button } from '../common';
import { useMoney } from '../../hooks/useMoney';

/**
 * 공휴일 안내 모달 — ModalWrapper 재사용 (디자인 통일).
 * BugHerd #67: holiday.blocked=true 면 "운행 불가" 메시지로 분기.
 * BugHerd #87: blocked 모드 문구를 "서비스 점검 안내" 톤으로 부드럽게 교체. 다국어 i18n 적용.
 *   외부 클릭으로는 안 닫히지만, "날짜 다시 선택" 버튼으로는 닫혀야 사용자가 다른 날짜 고를 수 있음.
 * BugHerd #87 회귀 fix(2026-05-11): surcharge 케이스(amount>0)에서 onConfirm 콜백 지원.
 *   확인 버튼 → onConfirm() 호출 후 onClose() — 호출부가 검색을 이어서 진행할 수 있도록.
 */
export default function HolidayWarningModal({ holiday, onClose, onConfirm }) {
  const { t } = useTranslation();
  const money = useMoney();
  if (!holiday) return null;
  const blocked = !!holiday.blocked;
  return (
    <ModalWrapper onClose={blocked ? () => {} : onClose} variant="compact" maxWidth="modal">
      <div className="flex items-center justify-between">
        <h2 className="text-base sm:text-lg font-bold text-mono-900">
          {blocked ? t('holiday_warning.blocked_title') : t('holiday_warning.surcharge_title')}
        </h2>
        {!blocked && <ModalCloseButton onClick={onClose} />}
      </div>
      <div
        className="text-2xs sm:text-2sm text-mono-700 leading-relaxed"
        style={{ wordBreak: 'keep-all', overflowWrap: 'break-word' }}
      >
        {blocked ? (
          <>
            <p>
              {t('holiday_warning.blocked_prefix')}{' '}
              <span className="font-bold text-mono-900 whitespace-nowrap">{holiday.date}</span>
              {t('holiday_warning.blocked_suffix')}
            </p>
            <p className="mt-3 text-2xs text-mono-500">
              {t('holiday_warning.select_another_date')}
            </p>
          </>
        ) : (
          <>
            <p>
              {t('holiday_warning.surcharge_prefix')}{' '}
              <span className="font-semibold text-mono-900 whitespace-nowrap">{holiday.date}</span>
              {t('holiday_warning.surcharge_suffix')}
            </p>
            <p className="mt-1.5">
              {t('holiday_warning.surcharge_intro')}
            </p>
            <p className="mt-3">
              {t('holiday_warning.surcharge_label')}{' '}
              <span className="font-bold text-orange-600 whitespace-nowrap">+{money(holiday.amount)}</span>
            </p>
            <p className="mt-3 text-2xs text-mono-500">
              {t('holiday_warning.surcharge_note')}
            </p>
          </>
        )}
      </div>
      <Button level="level1" fullWidth onClick={blocked ? onClose : () => { onConfirm?.(); onClose(); }}>
        {blocked ? t('holiday_warning.cta_reselect') : t('holiday_warning.cta_confirm')}
      </Button>
    </ModalWrapper>
  );
}
